import { type OtcConfig, type Quote, getOtcConfig, getQuote } from '@/services/api'
import { useCallback, useEffect, useRef, useState } from 'react'

export type UsePurchaseQuoteOptions = Record<string, never>

export interface UsePurchaseQuoteReturn {
  // 現在の見積もり（1 ARXあたりのレート情報）
  quote: Quote | null
  // OTC設定
  config: OtcConfig | null
  // ローディング状態
  isLoading: boolean
  // エラー
  error: string | null
  // 残り時間（秒）
  remainingSeconds: number
  // 見積もりが有効かどうか
  isQuoteValid: boolean
  // 見積もりを取得する関数（レート取得用、常に1 ARX）
  fetchQuote: () => Promise<void>
  // 見積もりをサイレントに更新する関数（ローディング表示なし）
  refetchQuote: () => Promise<void>
  // 見積もりをクリアする関数
  clearQuote: () => void
}

export function usePurchaseQuote(_options: UsePurchaseQuoteOptions = {}): UsePurchaseQuoteReturn {
  const [quote, setQuote] = useState<Quote | null>(null)
  const [config, setConfig] = useState<OtcConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState(0)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // OTC設定を取得
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getOtcConfig()
        if (response.success) {
          setConfig(response.config)
        }
      } catch (err) {
        console.error('Failed to fetch OTC config:', err)
      }
    }
    fetchConfig()
  }, [])

  // カウントダウンタイマー
  useEffect(() => {
    if (!quote) {
      setRemainingSeconds(0)
      return
    }

    const expiresAt = new Date(quote.expiresAt).getTime()

    const updateRemaining = () => {
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000))
      setRemainingSeconds(remaining)
    }

    updateRemaining()
    timerRef.current = setInterval(updateRemaining, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [quote])

  // クリーンアップ（タイマーのみ - abortはStrictModeで問題を起こすため削除）
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  // 見積もり取得の内部関数（showLoading: ローディング表示するかどうか）
  // 見積もりは常に1 ARXで取得され、レート表示用として使用される
  const fetchQuoteInternal = useCallback(async (showLoading: boolean) => {
    console.log('[usePurchaseQuote] fetchQuoteInternal called:', { showLoading })

    // 前のリクエストをキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    if (showLoading) {
      setIsLoading(true)
    }
    setError(null)

    try {
      const response = await getQuote(abortControllerRef.current.signal)
      console.log('[usePurchaseQuote] Quote response:', response)

      if (response.success) {
        setQuote(response.quote)
      } else {
        setError(response.error || 'Failed to get quote')
        if (showLoading) {
          setQuote(null)
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // リクエストがキャンセルされた場合は無視（StrictModeで発生）
        return
      }
      console.error('[usePurchaseQuote] Error fetching quote:', err)
      setError(err instanceof Error ? err.message : 'Failed to get quote')
      if (showLoading) {
        setQuote(null)
      }
    } finally {
      if (showLoading) {
        setIsLoading(false)
      }
    }
  }, [])

  // 通常の見積もり取得（ローディング表示あり）
  const fetchQuote = useCallback(async () => {
    await fetchQuoteInternal(true)
  }, [fetchQuoteInternal])

  // サイレントリフェッチ（ローディング表示なし、現在の見積もりを維持）
  const refetchQuote = useCallback(async () => {
    await fetchQuoteInternal(false)
  }, [fetchQuoteInternal])

  const clearQuote = useCallback(() => {
    setQuote(null)
    setError(null)
    setRemainingSeconds(0)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  const isQuoteValid = quote !== null && remainingSeconds > 0

  return {
    quote,
    config,
    isLoading,
    error,
    remainingSeconds,
    isQuoteValid,
    fetchQuote,
    refetchQuote,
    clearQuote,
  }
}
